// src/pages/TabletModel.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { FaTools, FaSearch } from 'react-icons/fa';
import RepairsModal from '../components/RepairsModal';

function TabletModel() {
    const { brand: brandUrlParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedTablet, setSelectedTablet] = useState(null);
    const [repairsData, setRepairsData] = useState([]);
    const [showRepairsModal, setShowRepairsModal] = useState(false);
    const [isLoadingRepairs, setIsLoadingRepairs] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [hasOpenedModalFromUrl, setHasOpenedModalFromUrl] = useState(false);

    const getTabletDbBrand = useCallback((urlBrand) => {
        const lower = urlBrand?.toLowerCase();
        switch (lower) {
            case 'ipad': return 'Apple';
            case 'samsung': return 'Samsung';
            case 'microsoft': return 'Microsoft';
            case 'amazon': return 'Amazon';
            case 'lenovo': return 'Lenovo';
            default: return urlBrand;
        }
    }, []);

    // Function to extract or determine tablet release year
    const getTabletReleaseYear = useCallback((tabletName, brand) => {
        const name = tabletName.toLowerCase();
        
        // Tablet model to year mapping for known models
        const tabletYearMapping = {
            // iPad models
            'ipad pro 12.9 5th gen': 2021,
            'ipad pro 12.9 4th gen': 2020,
            'ipad pro 12.9 3rd gen': 2018,
            'ipad pro 12.9 2nd gen': 2017,
            'ipad pro 12.9 1st gen': 2015,
            'ipad pro 11 3rd gen': 2021,
            'ipad pro 11 2nd gen': 2020,
            'ipad pro 11': 2018,
            'ipad pro 10.5': 2017,
            'ipad pro 9.7': 2016,
            'ipad 10th gen': 2022,
            'ipad 9th gen': 2021,
            'ipad 8th gen': 2020,
            'ipad 7th gen': 2019,
            'ipad 6th gen': 2018,
            'ipad 5th gen': 2017,
            'ipad 4th gen': 2012,
            'ipad 3rd gen': 2012,
            'ipad 3th gen': 2012,
            'ipad 2th gen': 2011,
            'ipad air 5th gen': 2022,
            'ipad air 4th gen': 2020,
            'ipad air 3rd gen': 2019,
            'ipad air 2': 2014,
            'ipad air': 2013,
            'ipad mini 6': 2021,
            'ipad mini 5': 2019,
            'ipad mini 4': 2015,
            'ipad mini 3': 2014,
            'ipad mini 2': 2013,
            'ipad mini 1': 2012,
            
            // Samsung Galaxy Tab Series
            'galaxy tab s9 ultra': 2023,
            'galaxy tab s9+': 2023,
            'galaxy tab s9': 2023,
            'galaxy tab s8 ultra': 2022,
            'galaxy tab s8+': 2022,
            'galaxy tab s8': 2022,
            'galaxy tab s7 fe': 2021,
            'galaxy tab s7+': 2020,
            'galaxy tab s7': 2020,
            'galaxy tab s6 lite': 2020,
            'galaxy tab s6': 2019,
            'galaxy tab a8': 2021,
            'galaxy tab a7 lite': 2021,
            'galaxy tab a7': 2020,
            'galaxy tab a 8.0': 2019,
            'samsung tab a6 2019': 2019,
            'samsung tab a6': 2016,
        };
        
        // Check exact match first
        const exactMatch = tabletYearMapping[name];
        if (exactMatch) return exactMatch;
        
        // Try partial matching for variations
        for (const [modelName, year] of Object.entries(tabletYearMapping)) {
            if (name.includes(modelName) || modelName.includes(name)) {
                return year;
            }
        }
        
        // Extract year from tablet name (look for 4-digit years)
        const yearMatch = name.match(/\b(20[0-9]{2})\b/);
        if (yearMatch) {
            return parseInt(yearMatch[1]);
        }
        
        // Extract generation numbers for iPads
        if (brand === 'Apple') {
            const genMatch = name.match(/(\d+)(st|nd|rd|th)\s*gen/i);
            if (genMatch) {
                const gen = parseInt(genMatch[1]);
                if (name.includes('ipad pro')) {
                    return 2015 + (gen - 1) * 2; // Rough estimate
                } else if (name.includes('ipad mini')) {
                    return 2012 + (gen - 1) * 2;
                } else if (name.includes('ipad air')) {
                    return 2013 + (gen - 1) * 2;
                } else {
                    return 2010 + gen; // Regular iPad
                }
            }
        }
        
        // Default to 2020 if year cannot be determined
        return 2020;
    }, []);

    // Function to sort tablets by year (latest first)
    const sortTabletsByYear = useCallback((tablets) => {
        return [...tablets].sort((a, b) => {
            const yearA = getTabletReleaseYear(a.model || a.name, a.brand);
            const yearB = getTabletReleaseYear(b.model || b.name, b.brand);
            
            // Sort by year descending (latest first)
            if (yearB !== yearA) {
                return yearB - yearA;
            }
            
            // If same year, sort by name
            const nameA = (a.model || a.name).toLowerCase();
            const nameB = (b.model || b.name).toLowerCase();
            
            // For same year, put larger screens first
            const getSizePriority = (name) => {
                if (name.includes('12.9')) return 5;
                if (name.includes('11')) return 4;
                if (name.includes('10.5')) return 3;
                if (name.includes('9.7')) return 2;
                if (name.includes('mini')) return 1;
                return 2;
            };
            
            const priorityA = getSizePriority(nameA);
            const priorityB = getSizePriority(nameB);
            
            if (priorityB !== priorityA) {
                return priorityB - priorityA;
            }
            
            return nameA.localeCompare(nameB);
        });
    }, [getTabletReleaseYear]);

    // Fetch tablets from API based on brand
    useEffect(() => {
        const fetchTablets = async () => {
            setLoading(true);
            setError(null);
            try {
                const dbBrand = getTabletDbBrand(brandUrlParam);
                const response = await fetch('/api/tablets');
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch tablets: ${response.status}`);
                }
                
                const data = await response.json();
                
                // Filter tablets by brand and map to expected structure
                const brandTablets = data
                    .filter(tablet => tablet.brand.toLowerCase() === dbBrand.toLowerCase())
                    .map(tablet => ({
                        name: tablet.model,
                        model: tablet.model,
                        brand: tablet.brand,
                        image: tablet.image || '',
                        repairs: tablet.repairs || []
                    }));
                
                // Sort tablets by year (latest first)
                const sortedTablets = sortTabletsByYear(brandTablets);
                setModels(sortedTablets);
            } catch (err) {
                console.error('Error fetching tablets:', err);
                setError(err.message || 'Failed to load tablets');
                setModels([]);
            } finally {
                setLoading(false);
            }
        };

        if (brandUrlParam) {
            fetchTablets();
        }
    }, [brandUrlParam, getTabletDbBrand, sortTabletsByYear]);

    // Filter models based on search term
    const filteredModels = useMemo(() => {
        if (!searchTerm.trim()) return models;
        
        const searchLower = searchTerm.toLowerCase();
        const filteredModels = models.filter(model => 
            model.name.toLowerCase().includes(searchLower)
        );
        
        // Re-sort filtered results to maintain latest-to-oldest order
        return sortTabletsByYear(filteredModels);
    }, [models, searchTerm, sortTabletsByYear]);

    // Group Samsung tablets by series
    const groupedModels = useMemo(() => {
        if (brandUrlParam?.toLowerCase() === 'samsung') {
            const groups = {
                'Tab S Series': [],
                'Tab A Series': [],
                'Tab E Series': [],
                'Others': []
            };
            
            const modelsToGroup = searchTerm.trim() 
                ? models.filter(model => model.name.toLowerCase().includes(searchTerm.toLowerCase()))
                : models;
                
            modelsToGroup.forEach(model => {
                const lowerName = model.name.toLowerCase();
                if (lowerName.includes('tab s')) {
                    groups['Tab S Series'].push(model);
                } else if (lowerName.includes('tab a')) {
                    groups['Tab A Series'].push(model);
                } else if (lowerName.includes('tab e')) {
                    groups['Tab E Series'].push(model);
                } else {
                    groups['Others'].push(model);
                }
            });
            
            // Sort each group from latest to oldest
            Object.keys(groups).forEach(groupName => {
                groups[groupName] = sortTabletsByYear(groups[groupName]);
            });
            
            return groups;
        }
        return null;
    }, [brandUrlParam, models, searchTerm, sortTabletsByYear]);

    useEffect(() => {
        setSelectedTablet(null);
        setShowRepairsModal(false);
        setIsLoadingRepairs(false);
        setFetchError(null);
        setRepairsData([]);
        setSearchTerm('');
    }, [brandUrlParam]);

    const handleModelClick = useCallback(async (model) => {
        if (!model || isLoadingRepairs) return;
        setSelectedTablet(model);
        setIsLoadingRepairs(true);
        setFetchError(null);
        setRepairsData([]);
        setShowRepairsModal(true);
        
        try {
            // Use the repairs data that came with the tablet
            if (model.repairs && model.repairs.length > 0) {
                setRepairsData(model.repairs);
            } else {
                // Fallback to API call if repairs not included
                const dbBrand = model.brand || getTabletDbBrand(brandUrlParam);
                const url = `/api/tablets/repairs?brand=${dbBrand}&model=${encodeURIComponent(model.name)}`;
                console.log("Request URL:", url);
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Server error: ${response.status} ${response.statusText}`);
                const data = await response.json();
                setRepairsData(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching tablet repairs data:', error);
            setFetchError(error.message || 'Failed to load repair details.');
            setRepairsData([]);
        } finally {
            setIsLoadingRepairs(false);
        }
    }, [isLoadingRepairs, brandUrlParam, getTabletDbBrand]);

    const handleModelKeyDown = useCallback((event, model) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleModelClick(model);
        }
    }, [handleModelClick]);

    const handleCloseModal = useCallback(() => {
        setShowRepairsModal(false);
    }, []);

    // Check for model query parameter and auto-open modal
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const modelParam = params.get('model');
        
        if (modelParam && models.length > 0 && !isLoadingRepairs && !hasOpenedModalFromUrl) {
            setHasOpenedModalFromUrl(true);
            
            // Clear the query parameter immediately
            const newParams = new URLSearchParams(location.search);
            newParams.delete('model');
            const newSearch = newParams.toString();
            const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
            window.history.replaceState({}, '', newUrl);
            
            // Normalize the search query
            const normalizedParam = modelParam.toLowerCase().trim();
            
            // Find the matching model - EXACT MATCH ONLY
            const matchingModel = models.find(model => {
                const modelName = (model.name || model.model || '').toLowerCase().trim();
                
                // EXACT MATCH ONLY - case insensitive
                if (modelName === normalizedParam) {
                    console.log('✓ Exact match found:', modelName);
                    return true;
                }
                
                return false;
            });
            
            if (matchingModel) {
                console.log('Opening modal for tablet:', matchingModel.name);
                setTimeout(() => {
                    handleModelClick(matchingModel);
                }, 100);
            } else {
                // Show "Device not found" message
                console.log('❌ No exact match found for:', normalizedParam);
                
                // Show error notification
                const notification = document.createElement('div');
                notification.style.cssText = `
                    position: fixed;
                    top: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #dc3545;
                    color: white;
                    padding: 15px 25px;
                    border-radius: 8px;
                    z-index: 10000;
                    font-size: 14px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    animation: slideDown 0.3s ease;
                `;
                notification.innerHTML = `
                    <strong>❌ Device not found</strong><br>
                    "${modelParam}" is not available
                `;
                document.body.appendChild(notification);
                
                // Add animation
                const style = document.createElement('style');
                style.textContent = `
                    @keyframes slideDown {
                        from { transform: translate(-50%, -100%); opacity: 0; }
                        to { transform: translate(-50%, 0); opacity: 1; }
                    }
                `;
                document.head.appendChild(style);
                
                // Remove notification after 5 seconds
                setTimeout(() => {
                    notification.style.animation = 'slideDown 0.3s ease reverse';
                    setTimeout(() => {
                        document.body.removeChild(notification);
                        document.head.removeChild(style);
                    }, 300);
                }, 5000);
            }
        }
    }, [models, location.search, handleModelClick, isLoadingRepairs, hasOpenedModalFromUrl]);

    const getBrandDisplayName = (brand) => {
        const brandMap = {
            'ipad': 'iPad',
            'samsung': 'Samsung Galaxy Tab',
            'microsoft': 'Microsoft Surface',
            'amazon': 'Amazon Fire',
            'lenovo': 'Lenovo'
        };
        return brandMap[brand?.toLowerCase()] || brand;
    };

    const brandDisplayName = getBrandDisplayName(brandUrlParam);

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p>Loading {brandDisplayName} models...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.errorContainer}>
                <p style={styles.errorText}>Error: {error}</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Select Your {brandDisplayName} Model</h1>
            </div>

            {/* Search Bar */}
            <div style={styles.searchContainer}>
                <FaSearch style={styles.searchIcon} />
                <input
                    type="text"
                    placeholder={`Search ${brandDisplayName} models...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
            </div>

            {/* Results count */}
            {searchTerm && (
                <p style={styles.resultsCount}>
                    Found {filteredModels.length} model{filteredModels.length !== 1 ? 's' : ''}
                </p>
            )}

            {/* Models Grid */}
            {groupedModels ? (
                // Grouped display for Samsung
                <div style={styles.groupedContainer}>
                    {Object.entries(groupedModels).map(([groupName, groupModels]) => {
                        if (groupModels.length === 0) return null;
                        return (
                            <div key={groupName} style={styles.groupSection}>
                                <h2 style={styles.groupTitle}>{groupName}</h2>
                                <div style={styles.modelsGrid}>
                                    {groupModels.map((model, index) => (
                                        <div
                                            key={index}
                                            style={styles.modelCard}
                                            onClick={() => handleModelClick(model)}
                                            onKeyDown={(e) => handleModelKeyDown(e, model)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`Select ${model.name} for repairs`}
                                        >
                                            <img
                                                src={model.image || '/images/tablet-placeholder.png'}
                                                alt={model.name}
                                                style={styles.modelImage}
                                                onError={(e) => {
                                                    e.target.src = '/images/tablet-placeholder.png';
                                                }}
                                            />
                                            <p style={styles.modelName}>{model.name}</p>
                                            <div style={styles.repairIcon}>
                                                <FaTools />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                // Regular grid for other brands
                <div style={styles.modelsGrid}>
                    {filteredModels.length > 0 ? (
                        filteredModels.map((model, index) => (
                            <div
                                key={index}
                                style={styles.modelCard}
                                onClick={() => handleModelClick(model)}
                                onKeyDown={(e) => handleModelKeyDown(e, model)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                tabIndex={0}
                                role="button"
                                aria-label={`Select ${model.name} for repairs`}
                            >
                                <img
                                    src={model.image || '/images/tablet-placeholder.png'}
                                    alt={model.name}
                                    style={styles.modelImage}
                                    onError={(e) => {
                                        e.target.src = '/images/tablet-placeholder.png';
                                    }}
                                />
                                <p style={styles.modelName}>{model.name}</p>
                                <div style={styles.repairIcon}>
                                    <FaTools />
                                </div>
                            </div>
                        ))
                    ) : (
                        <p style={styles.noModelsText}>
                            {searchTerm 
                                ? `No ${brandDisplayName} models found matching "${searchTerm}"`
                                : `No ${brandDisplayName} models available`}
                        </p>
                    )}
                </div>
            )}

            {/* Repairs Modal */}
            {showRepairsModal && (
                <RepairsModal
                    show={showRepairsModal}
                    onClose={handleCloseModal}
                    watchModel={{
                        name: selectedTablet?.name || 'Device',
                        repairs: repairsData,
                        image: selectedTablet?.image
                    }}
                />
            )}
        </div>
    );
}

// Styles - Black and White Theme
const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa'
    },
    header: {
        marginBottom: '30px',
        textAlign: 'center'
    },
    backButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#6c757d',
        textDecoration: 'none',
        fontSize: '16px',
        marginBottom: '20px',
        transition: 'opacity 0.3s',
        '&:hover': {
            opacity: '0.7'
        }
    },
    title: {
        fontSize: '2.5em',
        fontWeight: 'bold',
        color: '#6c757d',
        margin: '0'
    },
    searchContainer: {
        position: 'relative',
        maxWidth: '500px',
        margin: '0 auto 30px',
    },
    searchIcon: {
        position: 'absolute',
        left: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#6c757d',
        fontSize: '18px'
    },
    searchInput: {
        width: '100%',
        padding: '12px 20px 12px 45px',
        fontSize: '16px',
        border: '2px solid #6c757d',
        borderRadius: '25px',
        outline: 'none',
        transition: 'border-color 0.3s',
        backgroundColor: '#fff',
        color: '#6c757d'
    },
    resultsCount: {
        textAlign: 'center',
        color: '#6c757d',
        marginBottom: '20px',
        fontSize: '14px'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '3px solid #ddd',
        borderTop: '3px solid #6c757d',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    errorContainer: {
        textAlign: 'center',
        padding: '40px',
        minHeight: '400px'
    },
    errorText: {
        color: '#6c757d',
        fontSize: '18px',
        marginBottom: '20px'
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#6c757d',
        textDecoration: 'none',
        fontSize: '16px',
        '&:hover': {
            opacity: '0.7'
        }
    },
    groupedContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '40px'
    },
    groupSection: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '20px',
        border: '2px solid #6c757d',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    },
    groupTitle: {
        fontSize: '1.8em',
        fontWeight: '600',
        color: '#6c757d',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '2px solid #6c757d'
    },
    modelsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '20px',
        justifyContent: 'center'
    },
    modelCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '15px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '2px solid #6c757d',
        position: 'relative',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
        }
    },
    modelImage: {
        width: '100px',
        height: '120px',
        objectFit: 'contain',
        marginBottom: '10px'
    },
    modelName: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#6c757d',
        margin: '10px 0 5px'
    },
    repairIcon: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: '#6c757d',
        color: '#fff',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        opacity: '0.9'
    },
    noModelsText: {
        textAlign: 'center',
        color: '#6c757d',
        fontSize: '18px',
        gridColumn: '1 / -1',
        padding: '40px'
    }
};

// Add keyframes for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;
document.head.appendChild(styleSheet);

export default TabletModel;